import {
  AuthCredential,
  AuthError,
  GoogleAuthProvider,
  linkWithCredential,
  OAuthCredential,
  OAuthProvider,
  User,
  UserCredential
} from 'firebase/auth';

import { getSsoCredentialFromError } from '@/configuration/ssoProviders';
import { endpoints as userEndpoints } from '@/services/user.service';

const ACCOUNT_EXISTS_CODE = 'auth/account-exists-with-different-credential';
const PROVIDER_ALREADY_LINKED_CODE = 'auth/provider-already-linked';

let pendingSsoCredential: AuthCredential | null = null;
let pendingSsoIdToken: string | null = null;
let pendingSsoAccessToken: string | null = null;
let pendingSsoLinkEmail: string | null = null;
let pendingSsoProviderId: string | null = null;
let shouldLinkSso = false;

export const getPendingSsoLinkEmail = (): string | null => pendingSsoLinkEmail;

export const clearPendingSsoLink = (): void => {
  pendingSsoCredential = null;
  pendingSsoIdToken = null;
  pendingSsoAccessToken = null;
  pendingSsoLinkEmail = null;
  pendingSsoProviderId = null;
  shouldLinkSso = false;
};

const storePendingFromCredential = (credential: OAuthCredential | null | undefined): void => {
  pendingSsoCredential = credential ?? null;
  pendingSsoIdToken = credential?.idToken ?? null;
  pendingSsoAccessToken = credential?.accessToken ?? null;
};

const credentialFromUserCredential = (
  result: UserCredential,
  providerFirebaseId: string
): OAuthCredential | null => {
  if (providerFirebaseId === 'google.com') {
    return GoogleAuthProvider.credentialFromResult(result);
  }
  return OAuthProvider.credentialFromResult(result);
};

export const getSsoIdTokenFromError = (error: unknown): string | null => {
  const credential = getSsoCredentialFromError(error);
  return credential?.idToken ?? null;
};

export const getOAuthAccessTokenFromError = (error: unknown): string | null => {
  const credential = getSsoCredentialFromError(error);
  return credential?.accessToken ?? null;
};

export const capturePendingSsoLink = (
  error: unknown,
  providerFirebaseId?: string | null
): boolean => {
  const authError = error as AuthError;
  if (authError?.code !== ACCOUNT_EXISTS_CODE) {
    return false;
  }

  storePendingFromCredential(getSsoCredentialFromError(authError));
  pendingSsoLinkEmail = authError.customData?.email ?? null;
  pendingSsoProviderId = providerFirebaseId?.trim() || null;
  shouldLinkSso = true;
  return true;
};

/** Microsoft popup succeeded, but the existing password account must be confirmed before linking. */
export const capturePendingEntraLink = (
  idToken: string,
  email: string | null | undefined,
  providerFirebaseId: string
): void => {
  pendingSsoCredential = null;
  pendingSsoIdToken = idToken;
  pendingSsoAccessToken = null;
  pendingSsoLinkEmail = email?.trim() || null;
  pendingSsoProviderId = providerFirebaseId.trim();
  shouldLinkSso = true;
};

/** After a successful SSO popup that still requires password verification to merge. */
export const capturePendingSsoLinkFromResult = (
  result: UserCredential,
  providerFirebaseId: string,
  email?: string | null
): void => {
  storePendingFromCredential(credentialFromUserCredential(result, providerFirebaseId));
  pendingSsoLinkEmail = email?.trim() || result.user.email || null;
  pendingSsoProviderId = providerFirebaseId.trim();
  shouldLinkSso = true;
};

const rebuildCredential = (providerId: string): AuthCredential | null => {
  if (!pendingSsoIdToken && !pendingSsoAccessToken) {
    return null;
  }

  try {
    if (providerId === 'google.com' && pendingSsoIdToken) {
      return GoogleAuthProvider.credential(pendingSsoIdToken, pendingSsoAccessToken ?? undefined);
    }

    const provider = new OAuthProvider(providerId);
    return provider.credential({
      idToken: pendingSsoIdToken ?? undefined,
      accessToken: pendingSsoAccessToken ?? undefined
    });
  } catch {
    return null;
  }
};

const isAlreadyLinkedError = (error: unknown): boolean =>
  (error as AuthError)?.code === PROVIDER_ALREADY_LINKED_CODE;

const linkViaAdminApi = async (): Promise<boolean> => {
  if (!pendingSsoProviderId || (!pendingSsoIdToken && !pendingSsoAccessToken)) {
    return false;
  }

  await userEndpoints.linkSso({
    providerId: pendingSsoProviderId,
    idToken: pendingSsoIdToken || undefined,
    accessToken: pendingSsoAccessToken || undefined
  });
  return true;
};

/**
 * After email/password sign-in, attach the pending SSO provider.
 * Prefers the first-popup credential / Admin link — does not open a second OAuth popup.
 */
export const linkPendingSsoCredential = async (user: User): Promise<boolean> => {
  if (!shouldLinkSso) {
    return false;
  }

  if (pendingSsoLinkEmail && user.email?.toLowerCase() !== pendingSsoLinkEmail.toLowerCase()) {
    clearPendingSsoLink();
    return false;
  }

  const providerId = pendingSsoProviderId;

  try {
    const credential = pendingSsoCredential ?? (providerId ? rebuildCredential(providerId) : null);

    if (credential) {
      try {
        await linkWithCredential(user, credential);
        return true;
      } catch (error) {
        if (isAlreadyLinkedError(error)) {
          return true;
        }
        // Client credential may be spent/expired after the password step — use Admin link.
      }
    }

    if (await linkViaAdminApi()) {
      return true;
    }

    return false;
  } catch (error) {
    if (isAlreadyLinkedError(error)) {
      return true;
    }
    throw error;
  } finally {
    clearPendingSsoLink();
  }
};
